import { useState, useEffect, useMemo } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useExperienceCategories - Hook to fetch admin-controlled experience categories
 * All categories are managed in the admin panel and synced dynamically
 * 
 * Returns:
 * - categories: Array of category objects with id, name, icon, color, type
 * - categoryMap: Object mapping category_id to category object
 * - categoryColors: Object mapping category_id to color classes for styling
 * - loading: Boolean loading state
 * - error: Error message if fetch failed
 * - refetch: Function to manually refetch categories
 */
export const useExperienceCategories = () => {
  const [categories, setCategories] = useState([]);
  const [eventPresets, setEventPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("session_token");
      const headers = token ? { "Authorization": `Bearer ${token}` } : {};
      
      const res = await fetch(`${API}/experience-categories`, { headers });
      if (!res.ok) throw new Error("Failed to fetch categories");
      
      const data = await res.json();
      setCategories(data.categories || []);
      setEventPresets(data.event_presets || []);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch experience categories:", err);
      setError(err.message);
      // Fallback to empty - no hardcoding
      setCategories([]);
      setEventPresets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Map category_id to category object for quick lookup
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach(cat => {
      map[cat.id] = cat;
      // Also map by lowercase for case-insensitive lookup
      map[cat.id?.toLowerCase()] = cat;
    });
    return map;
  }, [categories]);

  // Generate Tailwind-compatible color classes from hex colors
  const categoryColors = useMemo(() => {
    const colors = {};
    categories.forEach(cat => {
      // Convert hex to Tailwind-like classes
      // We'll use inline styles for the actual colors since they're dynamic
      colors[cat.id] = {
        hex: cat.color,
        bg: `bg-[${cat.color}]`,
        text: `text-[${cat.color}]`,
        border: `border-[${cat.color}]`,
        // For calendar dots and badges
        dot: cat.color,
        // Light versions (approximate)
        light: `bg-[${cat.color}20]`,
        lightHex: `${cat.color}20`
      };
    });
    return colors;
  }, [categories]);

  // Get only visible categories (filtered by admin settings + user access)
  const visibleCategories = useMemo(() => {
    return categories.filter(cat => cat.visible !== false);
  }, [categories]);

  // Get categories by type (all are services now)
  // Iter 9.40 Slice I.f - `style` (and any retail-only category) was originally
  // experience-only; Iter 9.47.l opens `style` to the "Plan a Service" picker so
  // users can plan style/retail experiences end-to-end (Brand & link / My sizes
  // block in PlanModals depends on it). Pure `looks` / `retail` tile entries
  // still hide.
  const serviceCategories = useMemo(() => {
    const EXPERIENCE_ONLY_IDS = new Set(["retail", "cat_retail", "looks", "cat_looks"]);
    return visibleCategories.filter(cat => {
      const cid = String(cat.id || "").toLowerCase();
      if (EXPERIENCE_ONLY_IDS.has(cid)) return false;
      if (cat.experience_only === true) return false;
      return true;
    });
  }, [visibleCategories]);

  // Event presets (for Plan for an Event modal)
  const visibleEventPresets = useMemo(() => {
    return eventPresets;
  }, [eventPresets]);

  return {
    categories,
    visibleCategories,
    serviceCategories,
    eventPresets,
    visibleEventPresets,
    categoryMap,
    categoryColors,
    loading,
    error,
    refetch: fetchCategories
  };
};

/**
 * getCategoryColor - Helper to get color for a category
 * Falls back to gray if category not found
 */
export const getCategoryColor = (categoryId, categoryColors) => {
  if (!categoryId || !categoryColors) return { hex: "#6B7280", lightHex: "#6B728020" };
  const color = categoryColors[categoryId] || categoryColors[categoryId?.toLowerCase()];
  return color || { hex: "#6B7280", lightHex: "#6B728020" };
};

/**
 * getCategoryIcon - Map icon names to lucide-react components
 * This mapping is necessary since we store icon names as strings in DB
 */
export const ICON_MAP = {
  "scissors": "Scissors",
  "sparkles": "Sparkles", 
  "palette": "Palette",
  "eye": "Eye",
  "utensils": "Utensils",
  "heart": "Heart",
  "cake": "Cake",
  "star": "Star",
  "graduation-cap": "GraduationCap",
  "party-popper": "PartyPopper",
  "shopping-bag": "ShoppingBag",
  "presentation": "Presentation",
  "trending-up": "TrendingUp",
  "calendar": "Calendar",
  "users": "Users",
  "building": "Building",
  "briefcase": "Briefcase",
  "leaf": "Leaf",
  "coffee": "Coffee",
  "camera": "Camera",
  "music": "Music",
  "dumbbell": "Dumbbell",
  "plane": "Plane",
  "home": "Home",
  "refresh-cw": "RefreshCw",
  // Default
  "default": "Sparkles"
};

export default useExperienceCategories;
